import { colors, textEllipsis, typo } from '@design-system/styles'
import styled from '@emotion/styled'
import { urlUtil } from '@mathflat/common/util'
import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import clsx from 'clsx'
import { addDays } from 'date-fns'
import { observer, useLocalObservable } from 'mobx-react'
import { useEffect, useRef } from 'react'
import Scrollbars from 'react-custom-scrollbars-2'
import { useHistory } from 'react-router'

import _Button from '@/component/atoms/Button/_Button/_Button'
import Checkbox from '@/component/atoms/Checkbox'
import { Icon } from '@/component/atoms/Icon'
import type { NoticeBoard, NoticeBoardColl } from '@/dto/Notice/NoticeBoard.ts'
import commonRepo from '@/repository/common.repo.ts'
import { RouteName } from '@/router'
import modalService from '@/service/modal.service.tsx'
import { localStorageService } from '@/service/storage.service'
type Props = {
  notices: NoticeBoardColl
}

const NoticeBoardModal = ({ notices }: Props) => {
  const threeDaysCloseRef = useRef(false)
  const history = useHistory()

  const store = useLocalObservable(() => ({
    selectedNotice: notices.list[0],
    watchedNotices: localStorage.getItem(`팝업_읽은_목록_${commonRepo.user?.teacherId}`)
      ? new Set(JSON.parse(localStorage.getItem(`팝업_읽은_목록_${commonRepo.user?.teacherId}`)!))
      : new Set([]),

    setSelectedNotice(notice: NoticeBoard) {
      store.selectedNotice = notice
    },

    watchNotice(noticeId: NoticeBoard['id']) {
      store.watchedNotices.add(noticeId)
      localStorage.setItem(
        `팝업_읽은_목록_${commonRepo.user?.teacherId}`,
        JSON.stringify([...watchedNotices]),
      )
    },
  }))

  const { selectedNotice, watchedNotices, setSelectedNotice, watchNotice } = store

  useEffect(() => {
    // 가장 상단 게시물 읽음 처리.
    watchNotice(selectedNotice.id)

    
    return () => {
      handleThreeDaysClose()
    }
  }, [])

  const handleThreeDaysClose = () => {
    const teacherId = commonRepo.user?.teacherId
    const today = new Date()
    const expireDate = addDays(today, 2)
    if (threeDaysCloseRef.current && teacherId) {
      localStorageService.setItem(`PERSIST_DO_NOT_SHOW_AGAIN_NOTICE_BOARD_${teacherId}`)({
        teacherId,
        expiredAt: expireDate.getTime(),
      })
          } else {
          }
  }

  const onClickNotice = (notice: NoticeBoard) => {
    setSelectedNotice(notice)
    watchNotice(notice.id)

      }

  const onClickLinkButton = (notice: NoticeBoard) => {
    
    const link = notice.popupLinkButton!.link.replace(
      new RegExp(
        `(https?:\\/\\/)?teacher(?:-dev|-staging)?\\.${domainProvider.TARGET_DOMAIN.replace(/\./g, '\\.')}`,
        'g',
      ),
      '',
    )
    if (urlUtil.isFullUrl(link)) {
      //full URL일 경우
      const fullUrl = urlUtil.getFullUrl(link)
      if (isTallyUrl(fullUrl)) {
        // Tally 링크는 개인정보 URL 노출 방지를 위해 자체 /tally-embed 페이지에서 iframe으로 렌더링
        window.open(`${RouteName.tallyEmbed}?url=${encodeURIComponent(fullUrl)}`, '_blank')
      } else {
        window.open(fullUrl, '_blank')
      }
    } else {
      //pathname일 경우
      history.push(link)
    }
    modalService.closeModal()
  }

  return (
    <S.Modal>
      <S.ListSection>
        <Scrollbars>
          {notices.list.map((notice) => {
            return (
              <S.Item
                className={clsx(
                  notice.isNew && 'new',
                  selectedNotice.id === notice.id && 'active',
                  watchedNotices.has(notice.id) && 'watched',
                  notices.isPinned(notice) && 'pin',
                )}
                key={notice.id}
                onClick={() => {
                  onClickNotice(notice)
                }}
              >
                {notices.isPinned(notice) && <Icon name="pin" size={20} className="pin-icon" />}
                <div className="badge-wrapper">
                  <span className="badge">{notice.korTag}</span>
                </div>
                <div className="text">
                  <p>{notice.title}</p>
                  {notice.isNew && <Icon name="new_tag" color="white" size={10} />}
                </div>
              </S.Item>
            )
          })}
        </Scrollbars>
      </S.ListSection>
      <S.ContentSection>
        {selectedNotice.mediaUrl && (
          <S.Media>
            {selectedNotice.mediaType === 'VIDEO' ? (
              <video src={selectedNotice.mediaUrl} muted autoPlay loop className="video" />
            ) : (
              <img src={selectedNotice.mediaUrl} alt="media" className="image" />
            )}
          </S.Media>
        )}
        <Scrollbars
          className={clsx('scrollbar', !selectedNotice.mediaUrl && 'only-text')}
          key={selectedNotice.id}
        >
          <S.Content>
            <div className="title">{selectedNotice.title}</div>
            <div
              className="text-content"
              dangerouslySetInnerHTML={{
                __html: selectedNotice.content,
              }}
            />
            {selectedNotice.popupLinkButton && (
              <div className="popup-link-button">
                <_Button
                  minWidth={100}
                  size="m"
                  theme="primary"
                  style={{
                    width: 'auto',
                    maxWidth: '216px',
                    padding: '0 16px',
                  }}
                  onClick={() => onClickLinkButton(selectedNotice)}
                >
                  {selectedNotice.popupLinkButton.title}
                </_Button>
              </div>
            )}
          </S.Content>
        </Scrollbars>
      </S.ContentSection>
      <S.ButtonWrap>
        <Checkbox
          className="option"
          defaultChecked={threeDaysCloseRef.current}
          text="3일 동안 보지 않기"
          onChange={(checked) => {
            threeDaysCloseRef.current = checked
          }}
        />

        <button
          className="close"
          onClick={() => {
            modalService.closeModal()
          }}
        >
          닫기
        </button>
      </S.ButtonWrap>
    </S.Modal>
  )
}

export default observer(NoticeBoardModal)

/**
 * URL이 Tally 도메인(tally.so 또는 *.tally.so)인지 엄격하게 검증한다.
 * hostname 비교로 `tally.so.evil.com` 같은 위장 도메인을 차단한다.
 */
const isTallyUrl = (url: string) => {
  try {
    const parsed = new URL(url)
    return parsed.hostname === 'tally.so' || parsed.hostname.endsWith('.tally.so')
  } catch {
    return false
  }
}

const S = {
  Modal: styled.div`
    display: flex;
    gap: 8px;
    position: relative;
    width: 860px;
    height: 572px;
    padding: 16px;
    background-color: white;
    border-radius: var(--Radius-100);

    .scrollbar {
      flex: auto;
      height: auto;
    }
  `,

  ListSection: styled.div`
    flex: 0 0 280px;
    height: 100%;
    border-radius: var(--Radius-200);
    border: 1px solid ${colors.gray.$300};
    overflow: hidden;
    background-color: white;
  `,

  ContentSection: styled.div`
    width: 100%;
    height: 100%;
    background-color: white;
    border-radius: var(--Radius-200);
    border: 1px solid ${colors.gray.$300};
    overflow: hidden;

    .scrollbar {
      flex: auto;
      height: 255px !important;

      &.only-text {
        flex: auto;
        height: 100% !important;
      }
    }
  `,

  Item: styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    height: 54px;
    padding: 0 20px;
    background-color: white;
    border-bottom: 1px solid ${colors.gray.$300};
    cursor: pointer;

    &.active {
      .text {
        font-weight: 700;
      }
    }

    &.pin {
      background-color: ${colors.gray.$100};
      color: ${colors.gray.$900};
    }

    &.new {
      background-color: var(--Colors-Green-50);

      .text {
        color: var(--Colors-Green-500, #22c55e);
      }
    }

    &.watched {
      background-color: ${colors.gray.$200};

      &.active {
        .text {
          color: ${colors.gray.$900};
        }
      }

      .badge {
        color: ${colors.gray.$600};
      }

      .text {
        color: ${colors.gray.$600};
      }
    }

    .pin-icon {
      flex: 0 0 20px;
      path:first-of-type {
        fill: var(--Colors-Green-400);
      }

      path:last-of-type {
        fill: var(--Colors-Green-300);
      }
    }

    .badge-wrapper {
      display: flex;
      align-items: center;
      flex: 0 0 35px;
    }

    .badge {
      width: fit-content;
      padding: 2px 4px;
      border-radius: var(--Radius-100);
      border: 1px solid ${colors.gray.$300};
      background-color: ${colors.gray.$100};
      ${typo.caption02};
      font-weight: 700;
      color: ${colors.gray.$700};
    }

    .text {
      display: flex;
      gap: 6px;
      align-items: center;
      ${typo.body02};
      color: ${colors.gray.$900};

      p {
        ${textEllipsis(1)};
      }

      svg {
        flex: 0 0 10px;
      }
    }
  `,

  Media: styled.div`
    background-color: white;
    border-bottom: 1px solid var(--Colors-Gray-100, #f3f4f6);
    height: 283px;

    .image {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }

    .video {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  `,

  Content: styled.div`
    display: flex;
    flex-direction: column;
    padding: 24px;

    .title {
      ${typo.heading05};
      font-weight: 700;
      margin-bottom: 12px;
    }

    .ql-align-center {
      text-align: center;
    }

    .ql-align-right {
      text-align: right;
    }

    .popup-link-button {
      display: flex;
      justify-content: center;
      width: 100%;
      padding: 24px 0 0 0;
    }
  `,

  ButtonWrap: styled.div`
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    display: flex;
    justify-content: space-between;
    transform: translateY(100%);
    z-index: 10000;
    background-color: transparent;

    .option {
      padding-top: 8px;
      z-index: 10000;
      margin-right: auto;
      color: #fff;
      ${typo.body01};
    }

    .close {
      padding-top: 8px;
      padding-left: 10px;
      z-index: 10000;
      color: #fff;
      margin-left: auto;
      ${typo.body01};
    }
  `,
}
